import {
  dateStringFormat,
  OnboardingStatus,
  OnboardingStepType,
  OrganizationOverviewFragment,
} from '@alice-financial/alice-sdk'
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
} from '@alice-financial/pretext-ui'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import * as React from 'react'
import { orgRootPath } from '../orgDashboard/organization/useOrg'
import { useOrgOnboardingSummary } from '../orgDashboard/useOrgOnboardingSummary'

// If defined on a particular org, these onboarding steps will be shown with the appropriate labels
const displayedOnboardingSteps: Array<{ stepType: OnboardingStepType; label: string }> = [
  { stepType: OnboardingStepType.OrderForm, label: 'Order form' },
  { stepType: OnboardingStepType.CompanyProfile, label: 'Company roles' },
  { stepType: OnboardingStepType.CompleteKyc, label: 'Company Representative' },
  { stepType: OnboardingStepType.ConnectPayroll, label: 'Payroll' },
  { stepType: OnboardingStepType.WorkLocations, label: 'Locations' },
  { stepType: OnboardingStepType.ConnectBilling, label: 'Billing' },
  { stepType: OnboardingStepType.ConnectBanking, label: 'Contributions Account' },
  { stepType: OnboardingStepType.SetLaunchDate, label: 'Contributions Account' },
]

// StatusIcon using checkbox-based icons like Healthcheck.tsx
const StatusIcon = ({ status }: { status: OnboardingStatus }) => {
  if (status === OnboardingStatus.Completed) return <CheckBoxIcon color="primary" fontSize="medium" />
  if (status === OnboardingStatus.NotStarted) {
    return <CheckBoxOutlineBlankIcon color="primary" />
  }
  if (status === OnboardingStatus.InProgress) {
    return <IndeterminateCheckBoxOutlinedIcon color="primary" />
  }
  if (status === OnboardingStatus.Blocked) {
    return <IndeterminateCheckBoxOutlinedIcon color="disabled" />
  }
}

// Healthcheck item component for card display
const HealthcheckItem = ({
  stepType,
  label,
  org,
}: {
  stepType: OnboardingStepType
  label: string
  org: OrganizationOverviewFragment
}) => {
  const healthcheckSummary = useOrgOnboardingSummary(org)
  const path = orgRootPath(org.id)

  if (!healthcheckSummary) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={0.5}>
        <CircularProgress size={20} />
        <Typography variant="body2">{label}</Typography>
      </Box>
    )
  }

  const statusResult = healthcheckSummary[stepType]
  if (!statusResult) return null

  return (
    <Grid
      container
      component={Link}
      gap={1}
      to={path}
      sx={(theme) => ({
        p: 1,
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: 1,
        '&:hover': {
          backgroundColor: alpha(theme.palette.action.hover, theme.palette.action.hoverOpacity),
        },
      })}
    >
      <StatusIcon status={statusResult} />
      <Grid flex={1}>
        <Typography variant="body2">{label}</Typography>
      </Grid>
      <Grid>
        <IconButton size="small" color="primary">
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  )
}

// Activity log component for onboarding workflow
const ActivityLog = ({ org }: { org: OrganizationOverviewFragment }) => {
  if (!org.onboardingWorkflow?.activityLog?.length) {
    return (
      <Box>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Activity
        </Typography>
        <Typography variant="caption" color="text.secondary">
          No activity yet
        </Typography>
      </Box>
    )
  }

  // Sort activity log by date (most recent first)
  const sortedActivityLog = org.onboardingWorkflow.activityLog.sort(
    (a, b) => new Date(b.when).getTime() - new Date(a.when).getTime()
  )

  // Group activities by day
  const groupedByDay = sortedActivityLog.reduce<Record<string, typeof sortedActivityLog>>(
    (groups, logEntry) => {
      const date = new Date(logEntry.when)
      const dayKey = date.toDateString() // e.g., "Mon Dec 15 2024"

      if (!groups[dayKey]) {
        groups[dayKey] = []
      }
      groups[dayKey].push(logEntry)

      return groups
    },
    {}
  )

  // Get the days in chronological order (most recent first)
  const sortedDays = Object.keys(groupedByDay).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())

  return (
    <Box>
      <Typography variant="body2" fontWeight="bold" gutterBottom>
        Activity
      </Typography>
      <Box
        sx={{
          maxHeight: '22rem',
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <List dense sx={{ py: 0 }}>
          {sortedDays.map((dayKey) => {
            const dayActivities = groupedByDay[dayKey]
            if (!dayActivities || dayActivities.length === 0) return null

            return (
              <React.Fragment key={dayKey}>
                {/* Day header using ListSubheader */}
                <ListSubheader
                  sx={{
                    minHeight: 12,
                    fontSize: '0.75rem',
                    backgroundColor: '#eee',
                    color: 'text.primary',
                  }}
                >
                  {dayActivities[0] && dateStringFormat.short(dayActivities[0].when)}
                </ListSubheader>

                {/* Activities for this day */}
                {dayActivities.map((logEntry, index) => (
                  <ListItem key={`${dayKey}-${index}`} sx={{ px: 2, py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Typography variant="caption" component="p" fontSize="0.75em">
                          {logEntry.what}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </React.Fragment>
            )
          })}
        </List>
      </Box>
    </Box>
  )
}

export const OnboardingOrgCard = ({ org }: { org: OrganizationOverviewFragment }) => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container flexWrap="nowrap">
          <Grid minWidth={60}>
            {org?.logo?.url && (
              <Box
                component="img"
                src={org.logo.url}
                alt="Logo"
                sx={{
                  display: 'block',
                  width: 48,
                  height: 48,
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            )}
          </Grid>
          <Grid flexGrow={1}>
            <Grid container justifyContent="space-between">
              <Grid size={6}>
                <Grid container alignItems="center" columnGap={1}>
                  <Typography variant="body2" fontWeight="bold">
                    <Link to={orgRootPath(org.id)} underline="hover">
                      {org.name || 'Unknown Organization'}
                    </Link>
                  </Typography>
                  {!org.contractSignedOn && <Chip label="Order form not signed" color="error" size="small" />}
                </Grid>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {org.contractSignedOn && (
                    <>
                      Onboarding started {dateStringFormat.long(org.contractSignedOn)} <br />
                    </>
                  )}
                </Typography>
                {org?.contractSignedOn && (
                  <Grid component="ul" p={0} m={0} size={12}>
                    {displayedOnboardingSteps.map(({ stepType: key, label }) => (
                      <Box component="li" key={key} sx={{ listStyleType: 'none' }}>
                        <HealthcheckItem stepType={key} label={label} org={org} />
                      </Box>
                    ))}
                  </Grid>
                )}
              </Grid>

              {org?.contractSignedOn && (
                <Grid size={{ xs: 5 }}>
                  <ActivityLog org={org} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
